/**
 * @file `AdminFleetLifecycleSub` — `SubscriptionContract` for the admin
 *       console's read-only fleet-lifecycle stream. Shared between the
 *       admin-side proxy handler (`bindWebSocket`) and the SPA's fleet
 *       Client (`WsClientTransport.subscribe`).
 * @module @labcraft/admin/shared/fleet-subscription
 *
 * The admin Hypervisors panel stays fresh (umbrella #1258 Part H / #1340)
 * by subscribing to the hypervisor's fleet-lifecycle fan-out
 * (`FleetLifecycleSub`, #1337) — one push per Machine lifecycle transition
 * across the whole fleet, regardless of pty attachment. The admin proxy
 * bridges the operator's browser to that owner stream over WG (loopback in
 * dev), forwarding each frame verbatim; the SPA invalidates its
 * machine-list cache on every frame so rows + action affordances track
 * reality instead of reconcile-polling.
 *
 * Read-only: the browser sends nothing (the client frame is the
 * forward-reserved `__never` union); every frame flows server → browser.
 *
 * The frame schemas are REUSED from `@labcraft/hypervisor/shared/
 * subscriptions` (Zod-only — no Firecracker backend pull, the same subpath
 * `hypervisor_routes.ts` already imports the lifecycle enum from) rather
 * than redeclared, so the admin stream can't drift from the owner's wire
 * shape (`.agents/rules/design.md` § "Follow the already-coherent side").
 */

import { defineSubscription, CoreString } from '@labcraft/core'
import { z } from 'zod'
import {
  FleetLifecycleClientFrameSchema,
  FleetLifecycleFrameSchema,
  type FleetLifecycleClientFrame,
  type FleetLifecycleFrame,
} from '@labcraft/hypervisor/shared/subscriptions'

export {
  FleetLifecycleClientFrameSchema,
  FleetLifecycleFrameSchema,
  type FleetLifecycleClientFrame,
  type FleetLifecycleFrame,
}

/** Path params for the admin fleet-lifecycle WS — none (a single stream). */
export const AdminFleetLifecycleSubParamsSchema = z.object({})

/** Parsed {@link AdminFleetLifecycleSubParamsSchema}. */
export type AdminFleetLifecycleSubParams = z.infer<
  typeof AdminFleetLifecycleSubParamsSchema
>

/**
 * `WS /api/hypervisor/lifecycle` — admin's read-only fleet-lifecycle
 * observer. The SPA dials it via
 * `ws.subscribe(AdminFleetLifecycleSub, { params: {} })`; the admin
 * service binds the same contract via `bindWebSocket` and bridges
 * (read-only) to the hypervisor's `FleetLifecycleSub`. Server → browser:
 * one `FleetLifecycleFrame` (id + new state + reason) per Machine
 * transition across the fleet. Browser → server is dropped (the operator
 * observes, never drives).
 */
export const AdminFleetLifecycleSub = defineSubscription({
  path: '/api/hypervisor/lifecycle',
  params: AdminFleetLifecycleSubParamsSchema,
  clientFrame: FleetLifecycleClientFrameSchema,
  serverFrame: FleetLifecycleFrameSchema,
  description: CoreString.oneLine`
    Read-only fleet-lifecycle observer. Browser dials admin; admin proxies to
    the hypervisor's fleet-lifecycle stream over WG (loopback in dev). Server →
    browser: one lifecycle frame (id + state + reason) per Machine transition.
    Browser → server is dropped (the operator observes, never drives).
  `,
})
